"use client";
import { KnowledgeSourcesPanel } from "@/components/knowledge-sources/knowledge-sources-panel"
import { useUiControlLabels } from "@/components/i18n/ui-control-labels-context"


import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Database,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Loader2,
  ExternalLink,
  X,
  Settings2,
} from "lucide-react";
import { useVoiceAgentConfig } from "@/hooks/agents/voice";
import { useAuth } from "@/hooks/use-auth";
import { SettingsTabLoader } from "./settings-tab-loader";
import { RagSettingsDialog } from "./rag-settings-dialog";
import { useKnowledgeBaseFoldersQuery } from "@/hooks/queries/knowledge-base-folders";
import {
  buildAgentKnowledgeBaseDisplayItems,
  documentsOfferedToAgents,
} from "@/lib/ai/knowledge-base/agent-folder-display";
import { KNOWLEDGE_BASE_HREF } from "@/lib/ai/knowledge-base/page-link";
import { KnowledgeBaseFolderRow } from "@/components/agents/shared/knowledge-base-folder-row";
import { useTranslations } from "next-intl"
import { TranslationText } from "@/components/i18n/translation-text"





interface KBDocument {
  id: string;
  title: string;
  category?: string;
  token_count?: number;
  is_active: boolean;
  folder_id?: string | null;
  source_type?: string | null;
  mode?: "rag" | "direct";
}

interface KnowledgeBaseSettingsProps {
  agentId: string;  // UUID
}

export function KnowledgeBaseSettings({ agentId }: KnowledgeBaseSettingsProps) {
  const {locale: uiFormattingLocale} = useUiControlLabels()



  const tUi = useTranslations("Voice.AgentsVoiceSettingsKnowledgeBaseSettings")

  const { fetchWithAuth, user } = useAuth();
  const {
    config,
    isLoading: isLoadingConfig,
    saveConfig,
    updateRagSettings,
  } = useVoiceAgentConfig(agentId);
  const foldersQuery = useKnowledgeBaseFoldersQuery();

  const [kbDocuments, setKbDocuments] = useState<KBDocument[]>([]);
  const [allKbDocuments, setAllKbDocuments] = useState<KBDocument[]>([]);
  const [isLoadingKB, setIsLoadingKB] = useState(false);
  const [syncingKBId, setSyncingKBId] = useState<string | null>(null);
  const [syncAction, setSyncAction] = useState<"adding" | "removing" | null>(null);
  const [syncMessage, setSyncMessage] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const alertRef = useRef<HTMLDivElement>(null);
  const [ragDialogOpen, setRagDialogOpen] = useState(false);

  const loadKBDocuments = useCallback(async (options?: { background?: boolean }) => {
    if (!options?.background) setIsLoadingKB(true);
    try {
      const response = await fetchWithAuth(
        "/api/ai/knowledge-base/documents",
      );
      if (response.ok) {
        const data = await response.json();
        const allDocs: KBDocument[] = documentsOfferedToAgents(data.documents || []);
        setAllKbDocuments(allDocs);
        setKbDocuments(allDocs.filter(document => document.is_active));
      }
    } catch (err) {
      console.error("Error loading KB documents:", err);
    } finally {
      setIsLoadingKB(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadKBDocuments();
  }, [loadKBDocuments]);

  const handleToggleKB = async (
    kbIds: string[],
    itemKey: string,
    isFolder = false,
  ) => {
    const currentKBs = config.knowledge_bases || [];
    const currentIds = new Set(currentKBs);
    const isRemoving = kbIds.every(kbId => currentIds.has(kbId));
    const toggledIds = new Set(kbIds);

    setSyncingKBId(itemKey);
    setSyncAction(isRemoving ? "removing" : "adding");
    setSyncMessage(null);

    try {
      const newKBs = isRemoving
        ? currentKBs.filter(id => !toggledIds.has(id))
        : [...new Set([...currentKBs, ...kbIds])];

      // Save config - this also updates local state from API response
      const saved = await saveConfig({ knowledge_bases: newKBs });
      if (!saved) {
        throw new Error("Failed to save config");
      }

      if (isFolder) {
        const response = await fetchWithAuth(
          `/api/agents/voice/${agentId}/sync-kb`,
          { method: "POST" }
        );
        const result = await response.json();

        if (
          response.ok
          && result.success
          && result.partialErrors?.length > 0
        ) {
          setSyncMessage({
            type: "error",
            message: `Some documents failed:\n${result.partialErrors.join("\n")}`,
          });
        } else if (response.ok && result.success) {
          setSyncMessage({
            type: "success",
            message: isRemoving
              ? "Knowledge base folder removed from agent"
              : "Knowledge base folder added to agent",
          });
        } else {
          await saveConfig({ knowledge_bases: currentKBs });
          setSyncMessage({
            type: "error",
            message: result.error || "Failed to sync knowledge base folder",
          });
        }
      } else if (isRemoving) {
        // Remove KB from agent and potentially from ElevenLabs
        const response = await fetchWithAuth(
          `/api/agents/voice/${agentId}/sync-kb?doc_id=${kbIds[0]}`,
          { method: "DELETE" }
        );
        const result = await response.json();

        if (response.ok) {
          setSyncMessage({
            type: "success",
            message: result.deleted_from_elevenlabs
              ? "Knowledge base removed from agent and cloud"
              : "Knowledge base removed from agent",
          });
        } else {
          // Rollback config on error
          await saveConfig({ knowledge_bases: currentKBs });
          setSyncMessage({
            type: "error",
            message: result.error || "Failed to remove knowledge base",
          });
        }
      } else {
        // Add KB to agent - sync to ElevenLabs
        const response = await fetchWithAuth(
          `/api/agents/voice/${agentId}/sync-kb`,
          { method: "POST" }
        );
        const result = await response.json();

        if (response.ok && result.success) {
          // Check if there were partial errors (some docs synced, some failed)
          if (result.partialErrors && result.partialErrors.length > 0) {
            // Partial success - don't rollback, but show warning
            setSyncMessage({
              type: "error",
              message: `Some documents failed:\n${result.partialErrors.join("\n")}`,
            });
          } else {
            // Use mode from document data
            const addedKb = kbDocuments.find((kb) => kb.id === kbIds[0]);
            const mode = addedKb?.mode === "rag" ? "RAG" : "Direct";
            setSyncMessage({
              type: "success",
              message: `Knowledge base added (${mode} mode)`,
            });
          }
        } else {
          // Full failure - rollback config
          await saveConfig({ knowledge_bases: currentKBs });
          setSyncMessage({
            type: "error",
            message: result.error || "Failed to add knowledge base",
          });
        }
      }
    } catch (error) {
      console.error("Error toggling KB:", error);
      // Rollback config on error
      await saveConfig({ knowledge_bases: currentKBs });
      setSyncMessage({
        type: "error",
        message: tUi("failedToSyncKnowledgeBase"),
      });
    } finally {
      setSyncingKBId(null);
      setSyncAction(null);
    }
  };

  // Auto-hide success messages after 5s, but keep errors visible
  // Scroll to error message when it appears
  useEffect(() => {
    if (!syncMessage) return;

    if (syncMessage.type === "error") {
      // Scroll to alert on error
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    } else {
      // Auto-hide success messages
      const timer = setTimeout(() => setSyncMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [syncMessage]);

  const selectedKnowledgeBases = useMemo(
    () => config.knowledge_bases || [],
    [config.knowledge_bases],
  );

  // Check if any selected KB uses RAG mode
  const hasRagDocuments = kbDocuments.some(
    (kb) => selectedKnowledgeBases.includes(kb.id) && kb.mode === "rag"
  );

  const displayItems = useMemo(
    () => buildAgentKnowledgeBaseDisplayItems({
      activeDocuments: kbDocuments,
      allDocuments: allKbDocuments,
      folders: foldersQuery.data?.folders ?? [],
      selectedDocumentIds: selectedKnowledgeBases,
    }),
    [allKbDocuments, foldersQuery.data?.folders, kbDocuments, selectedKnowledgeBases],
  );

  // Show loader until config and KB documents are loaded
  if (isLoadingConfig || isLoadingKB || foldersQuery.isLoading) {
    return <SettingsTabLoader message={tUi("loadingKnowledgeBase")} />;
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="flex flex-wrap items-center justify-between gap-3"><h2 className="text-xl font-semibold text-foreground"><span key={tUi("knowledgeBase")}>{tUi("knowledgeBase")}</span></h2>
          <KnowledgeSourcesPanel presentation="connect-button" agent={{ type: "voice", id: agentId }} onKnowledgeChanged={() => { void loadKBDocuments({ background: true }); void foldersQuery.refetch() }} />
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          <span key={tUi("connectDocumentsToProvideContextAwareResponsesDuring")}>{tUi("connectDocumentsToProvideContextAwareResponsesDuring")}</span></p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Database className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <CardTitle className="text-lg"><span key={tUi("documents")}>{tUi("documents")}</span></CardTitle>
                  {selectedKnowledgeBases.length > 0 && (
                    <Badge
                      variant="outline"
                      className="text-purple-600 border-purple-600"
                    >
                      {selectedKnowledgeBases.length} <span><span key={tUi("selected")}>{tUi("selected")}</span></span>
                    </Badge>
                  )}
                </div>
                <CardDescription>
                  <span key={tUi("selectKnowledgeBasesForThisAgent")}>{tUi("selectKnowledgeBasesForThisAgent")}</span></CardDescription>
              </div>
            </div>
            {hasRagDocuments && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRagDialogOpen(true)}
                className="text-orange-600 border-orange-300 hover:bg-orange-50"
              >
                <Settings2 className="w-4 h-4 mr-1.5" />
                <span><span key={tUi("rAGConfig")}>{tUi("rAGConfig")}</span></span>
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {syncMessage && (
            <Alert
              ref={alertRef}
              className={
                syncMessage.type === "success"
                  ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950"
                  : "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950"
              }
            >
              <div className="flex items-start gap-3">
                {syncMessage.type === "success" ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                )}
                <AlertDescription
                  className={`flex-1 whitespace-pre-wrap ${
                    syncMessage.type === "success"
                      ? "text-green-800 dark:text-green-200"
                      : "text-red-800 dark:text-red-200"
                  }`}
                >
                  {syncMessage.message}
                </AlertDescription>
                {syncMessage.type === "error" && (
                  <button
                    onClick={() => setSyncMessage(null)}
                    className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </Alert>
          )}

          {displayItems.length === 0 ? (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <span key={tUi("noKnowledgeBaseDocumentsAvailableCreateDocumentsIn")}>{tUi("noKnowledgeBaseDocumentsAvailableCreateDocumentsIn")}</span></AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-2">
              {displayItems.map((item) => {
                if (item.kind === "folder") {
                  const isSyncingThis = syncingKBId === item.key;
                  const isDisabled = syncingKBId !== null;

                  return (
                    <KnowledgeBaseFolderRow
                      key={item.key}
                      name={item.folder.name}
                      sourceType={item.folder.source_type}
                      documentCount={item.folder.document_count}
                      selectedDocumentCount={item.selectedDocumentCount}
                      checked={item.isSelected}
                      isBusy={isSyncingThis}
                      disabled={isDisabled}
                      onToggle={() => handleToggleKB(item.documentIds, item.key, true)}
                    />
                  );
                }

                const kb = item.document;
                const isSelected = selectedKnowledgeBases.includes(kb.id);
                const isSyncingThis = syncingKBId === item.key;
                const isDisabled = syncingKBId !== null;

                return (
                  <label
                    key={item.key}
                    className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                      isDisabled
                        ? "opacity-60 cursor-not-allowed"
                        : "cursor-pointer"
                    } ${
                      isSelected
                        ? "border-purple-300 bg-purple-50"
                        : "border-gray-200 hover:bg-muted/50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? "bg-purple-200" : "bg-gray-100"
                        }`}
                      >
                        {isSyncingThis ? (
                          <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
                        ) : (
                          <FileText
                            className={`w-5 h-5 ${
                              isSelected ? "text-purple-600" : "text-gray-500"
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{kb.title}</span>
                          <Badge
                            variant="outline"
                            className={
                              kb.mode === "rag"
                                ? "text-orange-600 border-orange-300 bg-orange-50 text-xs"
                                : "text-blue-600 border-blue-300 bg-blue-50 text-xs"
                            }
                          >
                            <span key={kb.mode === "rag" ? "RAG" : tUi("direct")}>{kb.mode === "rag" ? "RAG" : tUi("direct")}</span>
                          </Badge>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {isSyncingThis ? (
                            syncAction === "adding" ? (
                              kb.mode === "rag" ? (
                                tUi("indexingThisMayTakeUpToAMinute")
                              ) : (
                                tUi("adding")
                              )
                            ) : (
                              tUi("removing")
                            )
                          ) : (
                            <>
                              {kb.category && <span>{kb.category}</span>}
                              {kb.category && kb.token_count && <span> · </span>}
                              {kb.token_count && <span><TranslationText>{tUi.rich("value0Tokens", { value0: String(kb.token_count.toLocaleString(uiFormattingLocale)) })}</TranslationText></span>}
                              {!kb.category && !kb.token_count && <span><span key={tUi("document")}>{tUi("document")}</span></span>}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <Switch
                      checked={isSelected}
                      disabled={isDisabled}
                      onCheckedChange={() => handleToggleKB(item.documentIds, item.key)}
                    />
                  </label>
                );
              })}
            </div>
          )}

          {user?.isManager && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() =>
                window.open(`/${KNOWLEDGE_BASE_HREF}`, "_blank")
              }
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              <span><span key={tUi("manageKnowledgeBase")}>{tUi("manageKnowledgeBase")}</span></span>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* RAG Settings Dialog */}
      <RagSettingsDialog
        open={ragDialogOpen}
        onOpenChange={setRagDialogOpen}
        agentId={agentId}
        hasRagDocuments={hasRagDocuments}
        currentSettings={config.rag_settings}
        onSettingsUpdated={updateRagSettings}
      />
    </div>
  );
}
